/**
 * Hook for migrating localStorage conversations to backend.
 */

import { useState, useCallback, useEffect } from 'react'
import { useConversations } from './useConversations'
import { apiClient } from '@/services/apiClient'

const MIGRATION_FLAG = 'ai-expert-conversations-migrated'
const OLD_STORAGE_KEY = 'ai-expert-chat-storage'

interface OldMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  sources?: unknown[]
  error?: string
}

interface OldConversation {
  id: string
  personaId: string | null
  title: string
  messages: OldMessage[]
  createdAt: number
  updatedAt: number
}

interface OldStorageState {
  conversations: Record<string, OldConversation>
  currentConversationId: string | null
}

interface UseMigrationReturn {
  isMigrating: boolean
  migrationError: string | null
  hasMigrated: boolean
  checkAndMigrate: () => Promise<void>
}

export function useMigration(): UseMigrationReturn {
  const [isMigrating, setIsMigrating] = useState(false)
  const [migrationError, setMigrationError] = useState<string | null>(null)
  const [hasMigrated, setHasMigrated] = useState(() => {
    return localStorage.getItem(MIGRATION_FLAG) === 'true'
  })

  const { fetchConversations } = useConversations()

  const checkAndMigrate = useCallback(async () => {
    // Already migrated
    if (localStorage.getItem(MIGRATION_FLAG) === 'true') {
      setHasMigrated(true)
      return
    }

    // Check for old data
    const oldDataStr = localStorage.getItem(OLD_STORAGE_KEY)
    if (!oldDataStr) {
      // No old data, mark as migrated
      localStorage.setItem(MIGRATION_FLAG, 'true')
      setHasMigrated(true)
      return
    }

    try {
      setIsMigrating(true)
      setMigrationError(null)

      // Parse old data (Zustand persist format)
      const parsed = JSON.parse(oldDataStr)
      const state: OldStorageState = parsed.state || parsed

      const conversations = Object.values(state.conversations || {})

      if (conversations.length === 0) {
        // No conversations to migrate
        localStorage.removeItem(OLD_STORAGE_KEY)
        localStorage.setItem(MIGRATION_FLAG, 'true')
        setHasMigrated(true)
        return
      }

      console.log(`[Migration] Migrating ${conversations.length} conversations to backend...`)

      // Transform for import API
      const toImport = conversations.map((conv) => ({
        id: conv.id,
        title: conv.title,
        persona_id: conv.personaId,
        messages: conv.messages.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          sources: msg.sources,
          error: msg.error,
        })),
        created_at: conv.createdAt,
        updated_at: conv.updatedAt,
      }))

      // Send to backend
      const response = await apiClient.fetch(`/api/conversations/import`, {
        method: 'POST',
        body: JSON.stringify(toImport),
      })

      if (!response.ok) {
        throw new Error(`Migration failed: ${response.statusText}`)
      }

      const imported = await response.json()
      console.log(`[Migration] Successfully migrated ${imported.length} conversations`)

      // Clear old storage and mark complete
      localStorage.removeItem(OLD_STORAGE_KEY)
      localStorage.setItem(MIGRATION_FLAG, 'true')
      setHasMigrated(true)

      // Refresh conversation list
      await fetchConversations()
    } catch (error) {
      console.error('[Migration] Error:', error)
      setMigrationError(error instanceof Error ? error.message : 'Migration failed')
    } finally {
      setIsMigrating(false)
    }
  }, [fetchConversations])

  // Auto-run migration on mount
  useEffect(() => {
    if (!hasMigrated) {
      checkAndMigrate()
    }
  }, [hasMigrated, checkAndMigrate])

  return {
    isMigrating,
    migrationError,
    hasMigrated,
    checkAndMigrate,
  }
}
